import { useCallback } from 'react';
import { Household, HouseholdStatus, User } from '../../../common/src/model.ts';
import * as server_api from '../../../common/src/server_api.ts';
import { OneDayMs } from '../../../common/src/sleep.ts';
import { isSameEmail } from '../../../common/src/util.ts';
import { synchronizeMobileHouseholdStatus } from '../util/billing.ts';
import { reconcileOptimisticSubscription } from '../util/optimistic_subscription.ts';
import { localStorageGet, LocalStorageKey, localStorageRemove, localStorageSet } from './client_local_storage.ts';
import { useLocalStorage } from './use_local_storage.tsx';

export function useHousehold(): {
  household: Household | undefined;
  setHousehold: (
    newValueOrFunction: (Household | undefined) | ((household: Household | undefined) => Household | undefined),
  ) => Promise<void>;
} {
  const [household, setHousehold] = useLocalStorage(LocalStorageKey.Household);

  const setHouseholdAndPushToServer = useCallback(
    async (
      newValueOrFunction: (Household | undefined) | ((household: Household | undefined) => Household | undefined),
    ) => {
      let newValue: Household | undefined;
      if (typeof newValueOrFunction === 'function') {
        newValue = newValueOrFunction(localStorageGet(LocalStorageKey.Household));
      } else {
        newValue = newValueOrFunction;
      }
      setHousehold(newValue);
      localStorageSet(LocalStorageKey.PushHousehold, true);
      await synchronizeHouseholdWithServer();
    },
    [setHousehold],
  );

  return { household, setHousehold: setHouseholdAndPushToServer };
}

let abortPreviousSync: undefined | (() => void) = undefined;

// syncs once per day unless forced
export async function synchronizeHouseholdWithServer({ force }: { force?: boolean } = {}) {
  try {
    let abort = false;
    if (abortPreviousSync) {
      abortPreviousSync();
      abortPreviousSync = undefined;
    }
    abortPreviousSync = () => {
      abort = true;
    };

    let household = localStorageGet(LocalStorageKey.Household);
    const pushHousehold = localStorageGet(LocalStorageKey.PushHousehold);

    // The Apple billing code had been creating households without a token -> need to nuke these to enable onboarding
    if (household && !household.token) {
      localStorageRemove(LocalStorageKey.Household);
      household = undefined;
    }

    if (
      household &&
      !force &&
      !pushHousehold &&
      household?.mostRecentSyncTimestamp &&
      Date.now() - household.mostRecentSyncTimestamp < OneDayMs
    ) {
      return;
    }

    if (household && pushHousehold && household?.token) {
      localStorageRemove(LocalStorageKey.PushHousehold);
      await server_api.upsertHousehold(household);
    }

    if (abort) {
      return;
    }

    // go ahead and pull down the server household even if we just pushed it up so everything is in sync
    if (household && household?.token) {
      const serverHousehold = await getHouseholdFromServer(household.token); // CAN THROW!
      if (abort) {
        return;
      }
      if (localStorageGet(LocalStorageKey.PushHousehold)) {
        // local household has been updated - abort sync & let it be reconciled on the next go round
        return;
      }
      if (serializeHousehold(household) !== serializeHousehold(serverHousehold)) {
        localStorageSet(LocalStorageKey.Household, serverHousehold);
      }
      reconcileOptimisticSubscription({ household: serverHousehold });
      // Clear active user if they are no longer in the household (e.g. deleted from server).
      // Note that this will force them to choose a different user.
      const activeUserToken = localStorageGet(LocalStorageKey.ActiveUserToken);
      if (serverHousehold && !serverHousehold.users?.some((user) => user.token === activeUserToken)) {
        localStorageRemove(LocalStorageKey.ActiveUserToken);
      }
    }

    // If the server says the household is subscribed, then we don't consult the client.
    // On iOS, this means a different household token can be used than the currently subscribed iOS token,
    // but only if the other token is subscribed.
    // If the other household token is not subscribed but iOS has a subscription, iOS will smash the
    // household token the one used for the iOS family subscription.
    // This enables people in the same iCloud household to be automatically configured with the same token.
    // Note that the household last-name & email can still be updated regardless.
    if (household?.status !== HouseholdStatus.Subscribed) {
      await synchronizeMobileHouseholdStatus();
    }
  } catch (e: any) {
    console.error(`Failed to sync household with server: ${e.message}`);
  }
}

function serializeHousehold(household: Household | undefined): string | undefined {
  if (!household) {
    return undefined;
  }
  // avoid taking into account the sync timestamp when comparing household contents
  const { mostRecentSyncTimestamp: _unused, ...householdWithoutTimestamp } = household;
  return JSON.stringify(householdWithoutTimestamp);
}

export async function getHouseholdFromServer(householdToken: string): Promise<Household | undefined> {
  const household = await server_api.getHousehold({ householdToken });
  if (!household) {
    return undefined;
  }
  setHouseholdStatus(household);
  household.mostRecentSyncTimestamp = Date.now();
  return household;
}

function setHouseholdStatus(household?: Household): HouseholdStatus | undefined {
  if (!household) {
    return;
  }
  // Note: the status never depends on the active user (GitHub issue #3399) - org admin/staff and internal
  // members ride on the server-computed household.isDiscounted instead, which grants full-library access
  // without touching the status that billing and onboarding depend on.
  if (household.subscriptionExpirationTimestamp && household.subscriptionExpirationTimestamp > new Date().getTime()) {
    household.status = HouseholdStatus.Subscribed;
  } else {
    household.status = HouseholdStatus.Unsubscribed;
  }
}

// The member who carries an email (a merged household's old email, or a member's own), or undefined unless
// exactly one does. Signing in by such an email makes that member the active user, so Choose User shows them
// already selected (GitHub issue #4104); the chooser still appears, since a shared family email may sit on one
// member while another person signs in.
export function memberForEmail(household: Household, email: string | undefined): User | undefined {
  const matches = (household.users ?? []).filter((user) => isSameEmail(user.email, email));
  return matches.length === 1 ? matches[0] : undefined;
}

// Whether email is a way in to household that nothing else keeps: set, not the household's own email (which signs
// in first), and carried by no member but exceptUserToken. Changing, clearing or deleting that member's email
// then stops it signing in, so the app asks first (GitHub issue #4104).
export function emailSignsInOnItsOwn(
  household: Household,
  email: string | undefined,
  { exceptUserToken }: { exceptUserToken?: string } = {},
): boolean {
  if (!email?.trim() || isSameEmail(email, household.householdEmail)) {
    return false;
  }
  return !memberCarriesEmail(household, email, { exceptUserToken });
}

// Whether a member of household other than exceptUserToken carries email, ignoring case and surrounding spaces
// (GitHub issue #4104)
export function memberCarriesEmail(
  household: Household,
  email: string | undefined,
  { exceptUserToken }: { exceptUserToken?: string } = {},
): boolean {
  return (household.users ?? []).some((user) => user.token !== exceptUserToken && isSameEmail(user.email, email));
}

// What the app asks before oldEmail stops signing in to the household, as a member's or the household's own email
// changes to newEmail or is cleared (GitHub issue #4104)
export function emailStopsSigningIn(oldEmail: string, newEmail?: string): string {
  return newEmail
    ? `${oldEmail} will no longer sign in to your household; ${newEmail} will. Continue?`
    : `${oldEmail} will no longer sign in to your household. Continue?`;
}

export function deleteHouseholdLocalStorageEntries() {
  localStorageRemove(LocalStorageKey.Household);
  localStorageRemove(LocalStorageKey.CachedOrgs);
  localStorageRemove(LocalStorageKey.WeeklySongList);
  localStorageRemove(LocalStorageKey.AllSongLists);
  localStorageRemove(LocalStorageKey.AllSongListsWrittenThrough);
  localStorageRemove(LocalStorageKey.ActiveUserToken);
  localStorageRemove(LocalStorageKey.CurrentOrgId);
}
